/**
 * Utility function for mapping ZITADEL user states to display-friendly labels.
 *
 * ZITADEL uses detailed user states internally, but the UI needs simpler
 * labels that make sense to organization admins.
 */

import type { UserState, UserStatus } from '../types/user';

/**
 * Maps a ZITADEL user state to a user-friendly display status.
 *
 * State mappings:
 * - USER_STATE_INITIAL → "Invited" (user created but hasn't logged in yet)
 * - USER_STATE_ACTIVE → "Active" (user has logged in and is active)
 * - USER_STATE_INACTIVE → "Inactive"
 * - USER_STATE_LOCKED → "Inactive"
 * - USER_STATE_SUSPEND → "Inactive"
 * - USER_STATE_DELETED → "Inactive"
 * - USER_STATE_UNSPECIFIED or unknown → "Inactive" (safe default)
 *
 * @param state - The raw ZITADEL user state, or undefined/null for unknown states
 * @returns A user-friendly status label
 *
 * @example
 * ```ts
 * mapUserStatus('USER_STATE_INITIAL') // 'Invited'
 * mapUserStatus('USER_STATE_ACTIVE') // 'Active'
 * mapUserStatus('USER_STATE_LOCKED') // 'Inactive'
 * mapUserStatus(undefined) // 'Inactive'
 * ```
 */
export function mapUserStatus(state: UserState | string | null | undefined): UserStatus {
  switch (state) {
    case 'USER_STATE_INITIAL':
      return 'Invited';

    case 'USER_STATE_ACTIVE':
      return 'Active';

    case 'USER_STATE_INACTIVE':
    case 'USER_STATE_LOCKED':
    case 'USER_STATE_SUSPEND':
    case 'USER_STATE_DELETED':
    case 'USER_STATE_UNSPECIFIED':
    default:
      return 'Inactive';
  }
}
